<template>
  <section class="max-w-[1440px] mx-auto px-12 py-12 space-y-12">

    <!-- HEADER -->
    <header>
      <h2 class="text-4xl font-black tracking-tight">
        Notifications
      </h2>
      <p class="text-slate-400 mt-2 max-w-2xl">
        Important updates about campaigns, influencers, and platform activity.
      </p>
    </header>

    <!-- EMPTY STATE -->
    <div
      v-if="filteredNotifications.length === 0"
      class="bg-white p-16 rounded-[2.5rem] premium-shadow
             border border-slate-50 text-center"
    >
      <h3 class="text-2xl font-bold text-slate-900">
        No notifications
      </h3>
      <p class="text-slate-400 mt-2">
        You're all caught up.
      </p>
    </div>

    <!-- NOTIFICATION LIST -->
    <section
      v-else
      class="bg-white rounded-[2.5rem] premium-shadow
             border border-slate-50 overflow-hidden"
    >
      <div
        v-for="n in filteredNotifications"
        :key="n.id"
        class="flex gap-6 px-8 py-6 border-b border-slate-50
               hover:bg-slate-50/60 transition"
        :class="!n.read ? 'bg-slate-50/40' : ''"
      >
        <!-- INDICATOR -->
        <div class="pt-2">
          <span
            class="block size-2 rounded-full"
            :class="n.read ? 'bg-slate-300' : 'bg-primary'"
          ></span>
        </div>

        <!-- CONTENT -->
        <div class="flex-1">
          <div class="flex items-center gap-3 mb-1">
            <span
              class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
              :class="typeClass(n.type)"
            >
              {{ n.type }}
            </span>

            <span class="text-xs text-slate-400">
              {{ n.time }}
            </span>
          </div>

          <p class="font-bold text-slate-900">
            {{ n.title }}
          </p>

          <p class="text-sm text-slate-500 mt-1 max-w-3xl">
            {{ n.message }}
          </p>
        </div>

        <!-- ACTION -->
        <div class="flex items-center">
          <button
            v-if="n.action"
            @click="open(n)"
            class="text-xs font-bold uppercase tracking-wider
                   border-b border-slate-900"
          >
            View
          </button>
        </div>
      </div>
    </section>

  </section>
</template>

<script>
import {
  loadNotificationsApi,
  markNotificationRead,
  markAllNotificationsRead
} from "../apis/notiapis";

export default {
  name: "NotificationAc",

  data() {
    return {
      filters: {
        type: "",
        read: ""
      },
      user_id:"",
      notifications: []
    };
  },

  async mounted() {
    const ldataRaw = sessionStorage.getItem("ldata");
    const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;
    if (ldata) {
      this.user_id = ldata.user_id;
    }
    this.loadNotifications();
  },

  computed: {
    filteredNotifications() {
      return this.notifications.filter(n => {

        const matchesType =
          !this.filters.type || n.type === this.filters.type;

        const matchesRead =
          !this.filters.read ||
          (this.filters.read === "read" && n.read) ||
          (this.filters.read === "unread" && !n.read);

        return matchesType && matchesRead;
      });
    }
  },

  methods: {

    // ✅ LOAD NOTIFICATIONS
    async loadNotifications() {

      const res = await loadNotificationsApi(this.user_id);

      if (res.status && res.records) {

        this.notifications = res.records.map(r => ({
          id: r.notification_id,
          type: r.noti_category,
          title: r.noti_title,
          message: r.noti_desc,
          time: r.noti_datetime,
          read: r.noti_type === "read",
          action: true
        }));
      }
    },

    typeClass(type) {
      if (type === "campaign") return "bg-slate-900 text-white";
      if (type === "influencer") return "bg-emerald-50 text-emerald-700";
      if (type === "system") return "bg-slate-100 text-slate-600";
      return "";
    },

    // ✅ MARK SINGLE READ
    async open(notification) {

      if (!notification.read) {

        await markNotificationRead(notification.id);

        notification.read = true;
      }

      alert(notification.title);
    },

    // ✅ MARK ALL READ
    async markAllRead() {

      const user_id = this.$store.state.user_id;

      const res = await markAllNotificationsRead(user_id);

      if (res.status) {
        this.notifications.forEach(n => (n.read = true));
      }
    }
  }
};

</script>

<style scoped>
.label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #94a3b8;
}

.input {
  margin-top: 6px;
  padding: 12px 14px;
  border-radius: 1rem;
  background: #f8fafc;
  font-weight: 600;
  min-width: 160px;
}

/* ===================================
   NOTIFICATION FILTER ALIGNMENT FIX
   =================================== */

/* Align filters neatly on desktop */
.bg-white.p-8.flex {
  align-items: flex-end;
}

.bg-white.p-8.flex > div {
  display: flex;
  flex-direction: column;
}

/* Push "Mark all as read" to right properly */
.bg-white.p-8.flex button {
  margin-bottom: 2px;
}


/* ===================================
   MOBILE RESPONSIVE PATCH
   =================================== */
@media (max-width: 640px) {

  /* Page padding */
  .px-12 {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .py-12 {
    padding-top: 24px !important;
    padding-bottom: 32px !important;
  }

  /* Header */
  header h2 {
    font-size: 26px !important;
    line-height: 1.2;
  }

  header p {
    font-size: 14px;
  }

  /* Filter bar → stacked cards */
  .bg-white.p-8.flex {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .bg-white.p-8.flex > div {
    width: 100%;
  }

  .bg-white.p-8.flex .input {
    width: 100%;
  }

  /* Mark all read button */
  .bg-white.p-8.flex button {
    margin-left: 0 !important;
    align-self: flex-start;
    font-size: 11px;
  }

  /* Notification rows */
  .px-8.py-6 {
    padding: 16px !important;
  }

  /* Reduce horizontal density */
  .flex.gap-6 {
    gap: 12px !important;
  }

  /* Title */
  .font-bold.text-slate-900 {
    font-size: 14px;
  }

  /* Message text */
  .text-sm {
    font-size: 13px;
    line-height: 1.5;
  }

  /* View action */
  .flex.items-center button {
    font-size: 11px;
  }

  /* Empty state */
  .p-16 {
    padding: 32px !important;
  }
}

</style>
